import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowUpDown,
  Clock,
  Loader2,
  MapPin,
  Radio,
  RefreshCw,
  Search,
  UserCheck,
  Users,
  X,
} from 'lucide-react'
import { adminApi } from '../../services/api'
import { useToast } from '../../contexts/ToastContext'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AvailableAgent {
  agentId: string
  firstName: string
  maskedPhone: string
  current_lat: number | null
  current_lng: number | null
  distance_km: number | null
  active_count: number
  out_of_radius?: boolean
  over_capacity?: boolean
  last_seen_at: string | null
}

interface AgentPickerDropdownProps {
  orderId: string
  currentAgentId?: string | null
  currentAgentName?: string | null
  onAssigned: () => void
  onClose: () => void
}

type SortField = 'distance' | 'active_count' | 'last_seen'

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const getInitials = (name: string | undefined) => {
  if (!name) return 'AG'
  return name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

const formatLastSeen = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms) || ms < 0) return ''
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  return `${Math.floor(hr / 24)}d ago`
}

const generateIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function AgentPickerDropdown({
  orderId,
  currentAgentId,
  currentAgentName: _currentAgentName,
  onAssigned,
  onClose,
}: AgentPickerDropdownProps) {
  const { addToast } = useToast()
  void _currentAgentName

  const [agents, setAgents] = useState<AvailableAgent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [radiusKm, setRadiusKm] = useState(5)
  const [sortField, setSortField] = useState<SortField>('distance')
  const [searchQuery, setSearchQuery] = useState('')
  const [assigningId, setAssigningId] = useState<string | null>(null)

  const idempotencyKeyRef = useRef(generateIdempotencyKey())

  // ── Fetch available agents ──────────────────────────────────────
  const fetchAgents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await adminApi.getAvailableAgents(orderId)
      const data = res?.data || res
      setAgents(Array.isArray(data?.agents) ? data.agents : [])
      if (data?.radius_km) setRadiusKm(data.radius_km)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load agents'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [orderId])

  useEffect(() => {
    void fetchAgents()
  }, [fetchAgents])

  // ── Sort + filter ───────────────────────────────────────────────
  const sortedAgents = useMemo(() => {
    let list = [...agents]

    // Client-side search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        (a) =>
          a.firstName.toLowerCase().includes(q) ||
          a.maskedPhone.includes(q),
      )
    }

    // Sort
    list.sort((a, b) => {
      switch (sortField) {
        case 'distance': {
          // Null distance (agent has no recent location) sorts last.
          const da = a.distance_km == null ? Infinity : a.distance_km
          const dbb = b.distance_km == null ? Infinity : b.distance_km
          return da - dbb
        }
        case 'active_count':
          return a.active_count - b.active_count
        case 'last_seen':
          return (
            new Date(b.last_seen_at || 0).getTime() -
            new Date(a.last_seen_at || 0).getTime()
          )
        default:
          return 0
      }
    })

    return list
  }, [agents, sortField, searchQuery])

  // ── Assign handler ──────────────────────────────────────────────
  const handleAssign = async (agentId: string) => {
    setAssigningId(agentId)
    try {
      // Use the universal PATCH /assign endpoint (assignAgent). It auto-detects
      // pickup vs drop from the order status and handles BOTH assignment and
      // reassignment in any phase (incl. ready / out_for_delivery / quality
      // check). The old POST endpoint (assignOrderToAgent) only accepted pickup
      // states, so reassigning a DELIVERY agent failed with "Invalid state for
      // assignment". This path is already proven in OrdersPage bulk-assign.
      await adminApi.assignAgent(orderId, agentId)
      addToast('Agent assigned successfully', 'success')
      onAssigned()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { code?: string; message?: string } } }
      if (axiosErr?.response?.data?.code === 'ALREADY_ASSIGNED') {
        addToast('This order was just assigned to another agent.', 'error')
        onAssigned()
      } else if (axiosErr?.response?.data?.code === 'AGENT_OFFLINE_ASSIGNMENT_DISABLED') {
        addToast('This agent is offline. Choose an online agent or enable offline pre-assignment.', 'error')
      } else {
        const msg = axiosErr?.response?.data?.message || 'Failed to assign agent'
        addToast(msg, 'error')
      }
    } finally {
      setAssigningId(null)
      // Regenerate key for next attempt
      idempotencyKeyRef.current = generateIdempotencyKey()
    }
  }

  // ── Styles ──────────────────────────────────────────────────────
  const sortChipStyle = (active: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '999px',
    border: active ? '1px solid #0F2E2A' : '1px solid #EAE4D8',
    background: active ? '#0F2E2A' : '#FFFFFF',
    color: active ? '#FFFFFF' : '#6B7280',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  })

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div
      style={{
        marginTop: '12px',
        background: '#FFFFFF',
        border: '1px solid #EAE4D8',
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '14px 16px 10px',
          borderBottom: '1px solid #EAE4D8',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p
            className="font-body"
            style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0F2E2A' }}
          >
            {currentAgentId ? 'Reassign Agent' : 'Assign Agent'}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close agent picker"
            style={{
              display: 'grid',
              placeItems: 'center',
              width: '24px',
              height: '24px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              borderRadius: '6px',
              color: '#9CA3AF',
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Search */}
        <div
          style={{
            position: 'relative',
            marginTop: '10px',
          }}
        >
          <Search
            size={14}
            style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9CAB9A',
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="font-body"
            aria-label="Search agents by name or phone"
            style={{
              width: '100%',
              height: '34px',
              paddingLeft: '30px',
              paddingRight: '10px',
              border: '1px solid #EAE4D8',
              borderRadius: '8px',
              fontSize: '12px',
              color: '#0F2E2A',
              background: '#FAFAF8',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Sort chips */}
        <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setSortField('distance')}
            className="font-body"
            style={sortChipStyle(sortField === 'distance')}
            aria-label="Sort by distance"
          >
            <MapPin size={10} /> Distance
          </button>
          <button
            type="button"
            onClick={() => setSortField('active_count')}
            className="font-body"
            style={sortChipStyle(sortField === 'active_count')}
            aria-label="Sort by active orders"
          >
            <Users size={10} /> Active
          </button>
          <button
            type="button"
            onClick={() => setSortField('last_seen')}
            className="font-body"
            style={sortChipStyle(sortField === 'last_seen')}
            aria-label="Sort by last seen"
          >
            <Clock size={10} /> Last seen
          </button>
        </div>
      </div>

      {/* Agent list */}
      <div style={{ maxHeight: '280px', overflowY: 'auto', padding: '4px' }}>
        {loading ? (
          // Skeleton rows
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={`skel-${i}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                borderRadius: '8px',
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '999px',
                  background: '#EAE4D8',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    width: '80px',
                    height: '12px',
                    borderRadius: '4px',
                    background: '#EAE4D8',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
                <div
                  style={{
                    width: '50px',
                    height: '10px',
                    borderRadius: '4px',
                    background: '#EAE4D8',
                    marginTop: '6px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
              </div>
            </div>
          ))
        ) : error ? (
          // Error state
          <div style={{ padding: '24px 16px', textAlign: 'center' }}>
            <p
              className="font-body"
              style={{ margin: 0, fontSize: '13px', color: '#991B1B' }}
            >
              {error}
            </p>
            <button
              type="button"
              onClick={fetchAgents}
              className="font-body"
              style={{
                marginTop: '10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 14px',
                borderRadius: '999px',
                border: '1px solid #EAE4D8',
                background: '#FFFFFF',
                color: '#0F2E2A',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <RefreshCw size={12} /> Retry
            </button>
          </div>
        ) : sortedAgents.length === 0 ? (
          // Empty state
          <div style={{ padding: '28px 16px', textAlign: 'center' }}>
            <Radio size={28} color="#9CAB9A" style={{ marginBottom: '8px' }} />
            <p
              className="font-body"
              style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#0F2E2A' }}
            >
              No agents available within {radiusKm} km
            </p>
            <p
              className="font-body"
              style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CAB9A' }}
            >
              Try again later or broadcast to a wider area
            </p>
            <button
              type="button"
              disabled
              className="font-body"
              aria-label="Broadcast to wider area (coming soon)"
              style={{
                marginTop: '12px',
                padding: '8px 18px',
                borderRadius: '999px',
                border: 'none',
                background: '#EAE4D8',
                color: '#9CAB9A',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'not-allowed',
                opacity: 0.6,
              }}
            >
              Broadcast (Phase 9)
            </button>
          </div>
        ) : (
          // Agent rows
          sortedAgents.map((agent) => {
            const isCurrentAgent = currentAgentId === agent.agentId
            const isAssigning = assigningId === agent.agentId

            return (
              <div
                key={agent.agentId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  marginBottom: '2px',
                  background: isCurrentAgent ? '#F0F9F6' : '#FFFFFF',
                  border: isCurrentAgent ? '1px solid #D6E8E0' : '1px solid transparent',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!isCurrentAgent) e.currentTarget.style.background = '#FAFAF8'
                }}
                onMouseLeave={(e) => {
                  if (!isCurrentAgent) e.currentTarget.style.background = '#FFFFFF'
                }}
              >
                {/* Avatar */}
                <div
                  style={{
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  <div
                    className="font-body"
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '999px',
                      background: '#D6B97B',
                      color: '#0F2E2A',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: '10px',
                      fontWeight: 700,
                    }}
                  >
                    {getInitials(agent.firstName)}
                  </div>
                  <span
                    style={{
                      position: 'absolute',
                      bottom: '-1px',
                      right: '-1px',
                      width: '8px',
                      height: '8px',
                      borderRadius: '999px',
                      background: '#15803D',
                      border: '2px solid #FFFFFF',
                    }}
                    aria-label="Online"
                  />
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <p
                      className="font-body"
                      style={{
                        margin: 0,
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#0F2E2A',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {agent.firstName}
                    </p>
                    {isCurrentAgent && (
                      <span
                        className="font-body"
                        style={{
                          padding: '1px 6px',
                          borderRadius: '999px',
                          background: '#0F2E2A',
                          color: '#FFFFFF',
                          fontSize: '9px',
                          fontWeight: 700,
                          letterSpacing: '0.3px',
                        }}
                      >
                        ASSIGNED
                      </span>
                    )}
                  </div>
                  <div
                    className="font-body"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '2px',
                      fontSize: '11px',
                      color: '#9CAB9A',
                    }}
                  >
                    <span>{agent.maskedPhone}</span>
                    {agent.distance_km != null ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                        <MapPin size={9} /> {Number(agent.distance_km).toFixed(1)} km
                      </span>
                    ) : null}
                    <span>{agent.active_count} active</span>
                    {agent.last_seen_at && (
                      <span>{formatLastSeen(agent.last_seen_at)}</span>
                    )}
                  </div>
                </div>

                {/* Action */}
                {isCurrentAgent ? (
                  <button
                    type="button"
                    onClick={() => handleAssign(agent.agentId)}
                    disabled={isAssigning}
                    className="font-body"
                    aria-label={`Reassign to ${agent.firstName}`}
                    style={{
                      padding: '5px 12px',
                      borderRadius: '999px',
                      border: '1px solid #EAE4D8',
                      background: '#FFFFFF',
                      color: '#0F2E2A',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: isAssigning ? 'not-allowed' : 'pointer',
                      opacity: isAssigning ? 0.6 : 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      flexShrink: 0,
                    }}
                  >
                    {isAssigning ? <Loader2 size={11} className="spin" /> : <ArrowUpDown size={11} />}
                    Reassign
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleAssign(agent.agentId)}
                    disabled={isAssigning}
                    className="font-body"
                    aria-label={`Assign ${agent.firstName}`}
                    style={{
                      padding: '5px 14px',
                      borderRadius: '999px',
                      border: 'none',
                      background: '#D6B97B',
                      color: '#0F2E2A',
                      fontSize: '11px',
                      fontWeight: 700,
                      cursor: isAssigning ? 'not-allowed' : 'pointer',
                      opacity: isAssigning ? 0.6 : 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      flexShrink: 0,
                      transition: 'opacity 0.15s',
                    }}
                  >
                    {isAssigning ? (
                      <Loader2 size={11} className="spin" />
                    ) : (
                      <UserCheck size={11} />
                    )}
                    Assign
                  </button>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Footer */}
      {!loading && !error && sortedAgents.length > 0 && (
        <div
          style={{
            padding: '8px 16px',
            borderTop: '1px solid #EAE4D8',
          }}
        >
          <p
            className="font-body"
            style={{
              margin: 0,
              fontSize: '11px',
              color: '#9CAB9A',
              textAlign: 'center',
            }}
          >
            {sortedAgents.length} agent{sortedAgents.length !== 1 ? 's' : ''} available within {radiusKm} km
          </p>
        </div>
      )}
    </div>
  )
}
